/**
 * RCA Question Data
 * Project Risk & Complexity Assessment
 *
 * All four assessment phases with their risk and complexity question sets.
 * Data is adapted from the Florida DMS-F-0505A assessment framework.
 *
 * IMPORTANT: Do NOT modify question text, weights, multipliers, or answer
 * options without formal change-control approval. These values are
 * DMS-F-0505A compliant.
 *
 * @version 2.0.0
 */

import type { Phase, PhaseId, Department } from '../types/rca';

// ============================================================
// DEPARTMENTS — Generic (customizable per organization)
// ============================================================

export const DEPARTMENTS: Department[] = [
  { code: 'EXEC', name: 'Executive Office' },
  { code: 'IT', name: 'Information Technology' },
  { code: 'FIN', name: 'Financial Services' },
  { code: 'OPS', name: 'Operations' },
  { code: 'HR', name: 'Human Resources' },
  { code: 'LEGAL', name: 'Legal & Compliance' },
  { code: 'COMM', name: 'Communications' },
  { code: 'PMO', name: 'Project Management Office' },
  { code: 'PROC', name: 'Procurement' },
  { code: 'INFRA', name: 'Infrastructure & Facilities' },
  { code: 'DATA', name: 'Data & Analytics' },
  { code: 'SEC', name: 'Security' },
  { code: 'OTHER', name: 'Other (specify below)' },
];

// ============================================================
// PHASE NAME MAP
// ============================================================

export const PHASE_NAMES: Record<PhaseId, string> = {
  pre_charter: 'Pre-Charter Phase',
  initiation: 'Initiation Phase',
  planning: 'Planning Phase',
  event_driven: 'Event-Driven Assessment',
};

// ============================================================
// CATEGORY REFERENCE DATA
// ============================================================

export const CATEGORY_NAMES: Record<number, string> = {
  1: 'Category 1 — Low',
  2: 'Category 2 — Moderate',
  3: 'Category 3 — High',
  4: 'Category 4 — Critical',
};

export const CATEGORY_DESCS: Record<number, string> = {
  1: 'Minimal project management controls required. Standard oversight and reporting.',
  2: 'Moderate project management controls required. Enhanced oversight, reporting, and governance.',
  3: 'Significant project management controls required. Rigorous oversight, mandatory artifacts, and governance review.',
  4: 'Maximum project management controls required. Full governance oversight, mandatory IV&V, and executive reporting.',
};

// ============================================================
// PHASES — All four assessment phases
// ============================================================

export const PHASES: Phase[] = [
  // ----------------------------------------------------------
  // PRE-CHARTER PHASE
  // ----------------------------------------------------------
  {
    id: 'pre_charter',
    name: 'Pre-Charter Phase',
    order: 1,
    desc: 'Performed at the beginning of the Initiation Phase. Reviews priorities, business need, and factors impacting project success.',
    risk: {
      totalWeight: 130,
      questions: [
        {
          num: '1',
          text: 'What is the estimated total project cost?',
          weight: 35,
          conversational: "Let's start with the budget. What's the estimated total cost of this project? Include everything -- hardware, software, licensing, labor, training, and any contingency funds.",
          chips: [
            { label: 'Under $250K', answerIndex: 4 },
            { label: '$250K-$500K', answerIndex: 3 },
            { label: '$500K-$1M', answerIndex: 2 },
            { label: '$1M-$5M', answerIndex: 1 },
            { label: 'Over $5M', answerIndex: 0 },
            { label: 'Not sure', answerIndex: -1 },
          ],
          answers: [
            { l: 'a', t: '> $5,000,000', m: 5 },
            { l: 'b', t: '$1,000,001 to $5,000,000', m: 4 },
            { l: 'c', t: '$500,001 to $1,000,000', m: 3 },
            { l: 'd', t: '$250,000 to $500,000', m: 2 },
            { l: 'e', t: '< $250,000', m: 1 },
          ],
        },
        {
          num: '2',
          text: 'How was the basis of estimate determined?',
          weight: 5,
          conversational: 'How was the cost estimate put together? Was it based on past projects, vendor quotes, a detailed breakdown, or more of a rough guess?',
          chips: [
            { label: 'Detailed WBS estimate', answerIndex: 3 },
            { label: 'Based on similar projects', answerIndex: 2 },
            { label: 'Professional judgment', answerIndex: 1 },
            { label: 'Other method', answerIndex: 0 },
          ],
          answers: [
            { l: 'a', t: 'Other methodology', m: 5 },
            { l: 'b', t: 'Consulting professional or department judgment', m: 4 },
            { l: 'c', t: 'Comparative (analogous) project evaluation', m: 2 },
            { l: 'd', t: 'Based on the sum of estimates of each WBS element (top down or bottom up)', m: 1 },
          ],
        },
        {
          num: '3',
          text: "How important is the project to meeting the department's strategic goals and objectives?",
          weight: 5,
          conversational: "How important is this project to your department's strategic goals? Is it mission-critical, or more of a nice-to-have?",
          chips: [
            { label: 'Critical', answerIndex: 0 },
            { label: 'Important', answerIndex: 1 },
            { label: 'Minimal impact', answerIndex: 2 },
          ],
          answers: [
            { l: 'a', t: 'Critical to meeting strategic goals and objectives', m: 5 },
            { l: 'b', t: 'Important to meeting strategic goals and objectives', m: 3 },
            { l: 'c', t: 'Little or no direct impact on strategic goals and objectives', m: 1 },
          ],
        },
        {
          num: '4',
          text: 'Has the department successfully executed projects with similar scope, schedule, and/or cost within the past two years?',
          weight: 10,
          conversational: 'Has your department delivered any similar projects in the past two years? I\'m looking for projects with comparable scope, budget, or timeline.',
          chips: [
            { label: 'Yes', answerIndex: 1 },
            { label: 'No', answerIndex: 0 },
            { label: 'Not sure', answerIndex: -1 },
          ],
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '5',
          text: 'What is the level of project management maturity within your organization?',
          weight: 15,
          conversational: "How would you describe your organization's project management maturity? Do you have established PM processes and templates, or is each project handled differently?",
          chips: [
            { label: 'Ad hoc', answerIndex: 0 },
            { label: 'Mixed', answerIndex: 1 },
            { label: 'Mostly mature', answerIndex: 2 },
            { label: 'Fully mature', answerIndex: 3 },
          ],
          answers: [
            { l: 'a', t: 'Not mature — mostly ad hoc project management processes', m: 5 },
            { l: 'b', t: 'Somewhat mature — an even mix of ad hoc and established, best-practice processes', m: 4 },
            { l: 'c', t: 'Moderately mature — using established processes, but not always consistently', m: 2 },
            { l: 'd', t: 'Mature — using established, best-practice processes consistently', m: 1 },
          ],
        },
        {
          num: '6',
          text: 'Does the project impact mission-critical supporting business processes?',
          weight: 10,
          conversational: 'Does this project touch any mission-critical business processes? For example, would a failure impact billing, public safety dispatch, or utility operations?',
          chips: [
            { label: 'Yes', answerIndex: 0 },
            { label: 'No', answerIndex: 1 },
          ],
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '7',
          text: 'What is the potential organizational impact to City departments with proceeding with this project?',
          weight: 10,
          conversational: "What's the organizational impact? Will this require changes to how multiple departments operate, just your department, or no process changes at all?",
          chips: [
            { label: 'Multiple departments', answerIndex: 0 },
            { label: 'Our department only', answerIndex: 1 },
            { label: 'No changes needed', answerIndex: 2 },
          ],
          answers: [
            { l: 'a', t: 'Requires re-engineering of organizations and processes affecting multiple departments', m: 5 },
            { l: 'b', t: 'Requires re-engineering of organizations and processes within our department only', m: 3 },
            { l: 'c', t: 'Requires no re-engineering of organizations and processes', m: 1 },
          ],
        },
        {
          num: '8',
          text: 'How critical is the project to meeting externally generated mandates (Executive, Legislative, or Regulatory)?',
          weight: 10,
          conversational: 'Is this project driven by an external mandate -- like a state regulation, federal requirement, or executive order? Or is it internally driven?',
          chips: [
            { label: 'Yes, mandatory', answerIndex: 0 },
            { label: 'No, internally driven', answerIndex: 1 },
          ],
          answers: [
            { l: 'a', t: 'Mandatory for accomplishment of external mandates', m: 5 },
            { l: 'b', t: 'Little or no direct impact on accomplishment of external mandates', m: 1 },
          ],
        },
        {
          num: '9',
          text: 'What is the level of certainty in the estimated scope of the project?',
          weight: 5,
          conversational: "How well-defined is the project scope right now? Is it locked down, or could it grow or shrink as you learn more?",
          chips: [
            { label: 'Could change', answerIndex: 0 },
            { label: 'Locked down', answerIndex: 1 },
          ],
          answers: [
            { l: 'a', t: 'Low — Scope could change (increase or decrease)', m: 5 },
            { l: 'b', t: 'High — Scope is clearly fixed and will not change', m: 1 },
          ],
        },
        {
          num: '10',
          text: 'What is the level of certainty in the estimated cost of the project?',
          weight: 5,
          conversational: 'How confident are you in the cost estimate? Is it based on hands-on experience, comparison to similar projects, or more of a ballpark figure?',
          chips: [
            { label: 'Low confidence', answerIndex: 0 },
            { label: 'Medium confidence', answerIndex: 1 },
            { label: 'High confidence', answerIndex: 2 },
          ],
          answers: [
            { l: 'a', t: 'Low — Cost estimate is not supported by experience or comparative analysis', m: 5 },
            { l: 'b', t: 'Medium — Cost estimate is based on comparative analysis of multiple similar projects', m: 3 },
            { l: 'c', t: 'High — Cost estimate based on hands-on experience and similar projects under similar conditions', m: 1 },
          ],
        },
        {
          num: '11',
          text: 'What is the level of certainty in the estimated duration of the project?',
          weight: 5,
          conversational: 'What about the timeline -- how confident are you in the estimated duration? Based on experience, comparison, or still uncertain?',
          chips: [
            { label: 'Low confidence', answerIndex: 0 },
            { label: 'Medium confidence', answerIndex: 1 },
            { label: 'High confidence', answerIndex: 2 },
          ],
          answers: [
            { l: 'a', t: 'Low — Duration estimate is not supported by experience or comparative analysis', m: 5 },
            { l: 'b', t: 'Medium — Duration estimate is based on comparative analysis of multiple similar projects', m: 3 },
            { l: 'c', t: 'High — Duration estimate based on hands-on experience and similar projects under similar conditions', m: 1 },
          ],
        },
        {
          num: '12',
          text: 'If the project uses budgeted funding, do project funds cross fiscal year budgets?',
          weight: 15,
          conversational: 'Does the project funding cross fiscal year boundaries? Will you need budget approval across multiple fiscal years, or is it contained in one?',
          chips: [
            { label: 'Yes, multi-year', answerIndex: 0 },
            { label: 'No, single year', answerIndex: 1 },
            { label: 'Not applicable', answerIndex: 2 },
          ],
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 3 },
            { l: 'c', t: 'This project does not use multi-year budgeted funding', m: 1 },
          ],
        },
      ],
    },
    complexity: {
      totalWeight: 185,
      questions: [
        {
          num: '1',
          text: 'Are department business processes directly impacted by the project?',
          weight: 10,
          help: 'Business processes that are "directly impacted" are those that are measurably affected by the project.',
          conversational: "Are your department's business processes directly impacted by this project? Will day-to-day operations measurably change?",
          chips: [
            { label: 'Yes', answerIndex: 0 },
            { label: 'No', answerIndex: 1 },
          ],
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '2',
          text: 'Are there interrelated projects that are dependent upon this project, or upon which this project depends (either for inputs, outputs, or resources)?',
          weight: 15,
          conversational: 'Are there other projects that depend on this one, or that this project depends on? Think about shared resources, data feeds, or sequenced deliverables.',
          chips: [
            { label: 'Both directions', answerIndex: 0 },
            { label: 'We depend on others', answerIndex: 1 },
            { label: 'Others depend on us', answerIndex: 2 },
            { label: 'No dependencies', answerIndex: 3 },
          ],
          answers: [
            { l: 'a', t: 'Dependent on other projects AND other projects dependent on this one', m: 5 },
            { l: 'b', t: 'Dependent on one or more other projects', m: 4 },
            { l: 'c', t: 'One or more other projects dependent on this project', m: 2 },
            { l: 'd', t: 'No interdependencies', m: 1 },
          ],
        },
        {
          num: '3',
          text: 'Are the organizational structure and functional responsibilities clearly defined for this project?',
          weight: 5,
          conversational: 'Is the organizational structure clearly defined for this project? Do people know their roles and responsibilities?',
          chips: [
            { label: 'Not defined', answerIndex: 0 },
            { label: 'Defined', answerIndex: 1 },
          ],
          answers: [
            { l: 'a', t: 'Not defined', m: 5 },
            { l: 'b', t: 'Defined', m: 1 },
          ],
        },
        {
          num: '4',
          text: "What role does the department's IT staff play in this project?",
          weight: 10,
          help: 'Consider: participates in governance, provides PM, ensures clear scope/requirements, provides technical SME.',
          conversational: "What role does your department's IT staff play? Are they involved in governance, project management, requirements, and technical expertise -- or just some of those?",
          chips: [
            { label: 'Minimal role', answerIndex: 0 },
            { label: 'Some involvement', answerIndex: 1 },
            { label: 'Significant', answerIndex: 2 },
            { label: 'Fully involved', answerIndex: 3 },
          ],
          answers: [
            { l: 'a', t: 'None or only one of the above', m: 5 },
            { l: 'b', t: 'Two of the above', m: 4 },
            { l: 'c', t: 'Three of the above', m: 2 },
            { l: 'd', t: 'All of the above', m: 1 },
          ],
        },
        {
          num: '5',
          text: 'Will this project drive a need for organizational change management?',
          weight: 15,
          conversational: 'Will this project require organizational change management? For example, will staff need to learn new systems, change workflows, or adopt new processes?',
          chips: [
            { label: 'Yes', answerIndex: 0 },
            { label: 'No', answerIndex: 1 },
          ],
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '6',
          text: 'Who are the primary customers for the potential solution?',
          weight: 15,
          conversational: 'Who are the primary users of whatever this project delivers? Citizens and the public, employees across multiple departments, or just your department?',
          chips: [
            { label: 'Citizens/public', answerIndex: 0 },
            { label: 'Multi-department', answerIndex: 1 },
            { label: 'Our department only', answerIndex: 2 },
          ],
          answers: [
            { l: 'a', t: 'The public / citizens', m: 5 },
            { l: 'b', t: 'Employees across multiple departments', m: 3 },
            { l: 'c', t: 'Employees within our department only', m: 1 },
          ],
        },
        {
          num: '7',
          text: 'Are multiple project procurements required?',
          weight: 15,
          conversational: 'Will you need multiple procurements for this project -- like separate contracts for software, hardware, consulting, etc.?',
          chips: [
            { label: 'Yes', answerIndex: 0 },
            { label: 'No', answerIndex: 1 },
          ],
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '8',
          text: "How much of the department's business is being reengineered as part of the project?",
          weight: 20,
          conversational: "How much of your department's business processes will be reengineered as part of this project? Give me a rough percentage.",
          chips: [
            { label: 'Over 75%', answerIndex: 0 },
            { label: '51-75%', answerIndex: 1 },
            { label: '26-50%', answerIndex: 2 },
            { label: 'Under 25%', answerIndex: 3 },
            { label: 'None', answerIndex: 4 },
          ],
          answers: [
            { l: 'a', t: '> 75% of business processes', m: 5 },
            { l: 'b', t: '51–75% of business processes', m: 4 },
            { l: 'c', t: '26–50% of business processes', m: 3 },
            { l: 'd', t: '0–25% of business processes', m: 2 },
            { l: 'e', t: 'No business process reengineering', m: 1 },
          ],
        },
        {
          num: '9',
          text: 'Are Subject Matter Experts available to participate in the definition of project requirements and scope?',
          weight: 20,
          conversational: 'Are subject matter experts available to help define project requirements and scope? People who really know the business processes inside and out?',
          chips: [
            { label: 'No', answerIndex: 0 },
            { label: 'Yes', answerIndex: 1 },
          ],
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '10',
          text: 'Are the operating procedures and business process diagrams that define the work currently accomplished accurate and up-to-date?',
          weight: 15,
          conversational: 'Are your current operating procedures and business process diagrams accurate and up-to-date? Or are they outdated or nonexistent?',
          chips: [
            { label: "Don't exist", answerIndex: 0 },
            { label: 'Partially documented', answerIndex: 1 },
            { label: 'All up to date', answerIndex: 2 },
          ],
          answers: [
            { l: 'a', t: 'Procedures and process flow diagrams do not exist', m: 5 },
            { l: 'b', t: 'Partially documented', m: 3 },
            { l: 'c', t: 'All up to date and validated', m: 1 },
          ],
        },
        {
          num: '11',
          text: 'Is documentation for the existing system kept up-to-date with system design documents, specifications, and O&M guides?',
          weight: 15,
          conversational: 'Is the documentation for the existing system kept current? Things like system design docs, specifications, and operations & maintenance guides.',
          chips: [
            { label: 'No', answerIndex: 0 },
            { label: 'Yes', answerIndex: 1 },
          ],
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '12',
          text: 'For the existing system, is trained staff available to interpret system behavior?',
          weight: 15,
          conversational: "Is there trained staff available who can interpret the existing system's behavior? People who understand why it does what it does?",
          chips: [
            { label: 'No', answerIndex: 0 },
            { label: 'Yes', answerIndex: 1 },
          ],
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '13',
          text: 'Will the project involve protected data (Employee, Citizen, etc.)?',
          weight: 15,
          conversational: 'Will this project involve protected data -- employee records, citizen information, financial data, health data, or anything similar?',
          chips: [
            { label: 'Yes', answerIndex: 0 },
            { label: 'No', answerIndex: 1 },
          ],
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
      ],
    },
  },

  // ----------------------------------------------------------
  // INITIATION PHASE
  // ----------------------------------------------------------
  {
    id: 'initiation',
    name: 'Initiation Phase',
    order: 2,
    desc: 'Performed at the end of the Initiation Phase following completion of initial project documentation. Confirms or adjusts risk & complexity level.',
    risk: {
      totalWeight: 330,
      questions: [
        {
          num: '1',
          text: 'What level of confidence does the Project Management Team have in the estimated cost of the project?',
          weight: 25,
          answers: [
            { l: 'a', t: 'Confidence ≤ 85%', m: 5 },
            { l: 'b', t: 'Confidence > 85% and ≤ 95%', m: 3 },
            { l: 'c', t: 'Confidence > 95% and ≤ 100%', m: 1 },
          ],
        },
        {
          num: '2',
          text: 'Is this project critical to support the primary functions for which the requesting department is responsible?',
          weight: 20,
          answers: [
            { l: 'a', t: "Directly involves effectiveness and efficiency of the department's primary functions", m: 5 },
            { l: 'b', t: "Contributes to enabling the department's primary functions", m: 3 },
            { l: 'c', t: "Indirectly impacts, or has minimal impact to the department's primary functions", m: 1 },
          ],
        },
        {
          num: '3',
          text: 'Is this project dependent on the deliverable(s) from another project, organization, or department?',
          weight: 15,
          answers: [
            { l: 'a', t: 'A project deliverable from another entity is required', m: 5 },
            { l: 'b', t: 'Will utilize other project deliverables', m: 4 },
            { l: 'c', t: 'Other deliverables will enhance the project', m: 2 },
            { l: 'd', t: 'No other deliverables are required', m: 1 },
          ],
        },
        {
          num: '4',
          text: 'Is the project dependent on limited resources controlled by an external entity?',
          weight: 15,
          answers: [
            { l: 'a', t: 'Requires external resources', m: 5 },
            { l: 'b', t: 'Requires no external resources', m: 1 },
          ],
        },
        {
          num: '5',
          text: 'Does the project sponsor have direct authority over all resources needed for the project (funding, equipment, facilities, human resources)?',
          weight: 15,
          answers: [
            { l: 'a', t: 'Authority over none of the resources', m: 5 },
            { l: 'b', t: 'Authority over some of the resources', m: 4 },
            { l: 'c', t: 'Authority over most of the resources', m: 2 },
            { l: 'd', t: 'Authority over all of the resources', m: 1 },
          ],
        },
        {
          num: '6',
          text: 'How will failure of the project impact the stakeholders?',
          weight: 15,
          answers: [
            { l: 'a', t: 'Impact is high', m: 5 },
            { l: 'b', t: 'Impact is between high and moderate', m: 3 },
            { l: 'c', t: 'Impact is between moderate and minimal', m: 1 },
          ],
        },
        {
          num: '7',
          text: 'Does the project impact the City at an enterprise level?',
          weight: 20,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '8',
          text: 'What is the level of assurance that stakeholders will deliver resources as promised?',
          weight: 5,
          answers: [
            { l: 'a', t: 'Minimal — no history of stakeholders delivering promised resources', m: 5 },
            { l: 'b', t: 'High — proven history of delivering all promised resources on time', m: 1 },
          ],
        },
        {
          num: '9',
          text: 'Are there any projected changes of critical or key stakeholders over the life of the project?',
          weight: 5,
          help: '"Critical Stakeholders" are essential to project success. "Key Stakeholders" are vital but their turnover is not directly tied to success.',
          answers: [
            { l: 'a', t: 'Yes, change of critical stakeholders anticipated', m: 5 },
            { l: 'b', t: 'Yes, change of key stakeholders anticipated', m: 3 },
            { l: 'c', t: 'No', m: 1 },
          ],
        },
        {
          num: '10',
          text: 'Is the assigned project manager certified by PMI (PgMP, PMP, CAPM, Agile Certified Practitioner, etc.)?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '11',
          text: 'Does the department have the necessary knowledge, skills, and abilities to staff the project team with in-house resources?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Some, but not all', m: 3 },
            { l: 'c', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '12',
          text: 'Do business users and subject matter experts have sufficient skills and experience given the size and complexity of the project?',
          weight: 10,
          answers: [
            { l: 'a', t: 'Never attempted a project of this size and complexity', m: 5 },
            { l: 'b', t: 'Skills and experience from previous projects, but not of similar size/complexity', m: 3 },
            { l: 'c', t: 'Extensive skills and experience from a previous project of similar size and complexity', m: 1 },
          ],
        },
        {
          num: '13',
          text: 'Does the assigned project manager have the specific experience (proven ability) to successfully execute a project of this scope and complexity?',
          weight: 20,
          answers: [
            { l: 'a', t: 'PM has never participated in a project of this scope and complexity', m: 5 },
            { l: 'b', t: 'PM managed a similar project but with smaller scope and complexity', m: 3 },
            { l: 'c', t: 'PM has managed a project of this scope and complexity', m: 1 },
          ],
        },
        {
          num: '14',
          text: 'What percent of the project team has experience with the selected development methodology or implementation approach?',
          weight: 10,
          answers: [
            { l: 'a', t: '< 50%', m: 5 },
            { l: 'b', t: '50–75%', m: 3 },
            { l: 'c', t: '76–100%', m: 1 },
          ],
        },
        {
          num: '15',
          text: "What percent of the department's IT leadership has experience with the development methodology or implementation approach?",
          weight: 5,
          answers: [
            { l: 'a', t: '< 50%', m: 5 },
            { l: 'b', t: '50–75%', m: 3 },
            { l: 'c', t: '76–100%', m: 1 },
          ],
        },
        {
          num: '16',
          text: 'How clearly defined and understood are the goals and objectives of this project by a majority of the project team and stakeholders?',
          weight: 25,
          answers: [
            { l: 'a', t: 'Vague and open to interpretation', m: 5 },
            { l: 'b', t: 'Well defined and understood', m: 1 },
          ],
        },
        {
          num: '17',
          text: 'Is the boundary between what is in scope and what is not clearly documented?',
          weight: 25,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '18',
          text: 'Has the project charter been reviewed and approved by all key stakeholders, including the project sponsor?',
          weight: 15,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '19',
          text: 'How was the estimated completion date for this project determined?',
          weight: 15,
          answers: [
            { l: 'a', t: 'Completion date has not yet been determined or estimated', m: 5 },
            { l: 'b', t: 'Driven by a defined time constraint', m: 3 },
            { l: 'c', t: 'No mandated constraint — schedule based on scope and resource availability', m: 1 },
          ],
        },
        {
          num: '20',
          text: 'What is the estimated budget for this project?',
          weight: 25,
          answers: [
            { l: 'a', t: 'Greater than $1 million', m: 5 },
            { l: 'b', t: 'Between $250,000 and $1 million', m: 3 },
            { l: 'c', t: 'Less than $250,000', m: 1 },
          ],
        },
        {
          num: '21',
          text: 'Are there multiple departments engaged as participants in this project?',
          weight: 25,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
      ],
    },
    complexity: {
      totalWeight: 230,
      questions: [
        {
          num: '1',
          text: 'What is the level of new technology or infrastructure impact required by the project?',
          weight: 20,
          answers: [
            { l: 'a', t: 'Significant level of new technologies or changes to critical systems', m: 5 },
            { l: 'b', t: 'Moderate level of new technologies or changes to critical systems', m: 3 },
            { l: 'c', t: 'Minimal-to-no new technologies or changes to critical systems', m: 1 },
          ],
        },
        {
          num: '2',
          text: 'What is the expected duration from acceptance of the Project Charter to the end of Execution Phase?',
          weight: 20,
          answers: [
            { l: 'a', t: '> 24 months', m: 5 },
            { l: 'b', t: '13–24 months', m: 4 },
            { l: 'c', t: '6–12 months', m: 2 },
            { l: 'd', t: '< 6 months', m: 1 },
          ],
        },
        {
          num: '3a',
          text: 'How many physical project team locations have to be managed?',
          weight: 5,
          parentLabel: 'Amount of resources being managed',
          answers: [
            { l: 'a', t: '> 4', m: 5 },
            { l: 'b', t: '1–4', m: 3 },
            { l: 'c', t: '1', m: 1 },
          ],
        },
        {
          num: '3b',
          text: 'How many physical locations are associated with the solution implementation?',
          weight: 5,
          answers: [
            { l: 'a', t: '> 25', m: 5 },
            { l: 'b', t: '6–25', m: 4 },
            { l: 'c', t: '2–5', m: 2 },
            { l: 'd', t: '1', m: 1 },
          ],
        },
        {
          num: '4',
          text: 'How many end users are going to be using the delivered product(s)?',
          weight: 15,
          answers: [
            { l: 'a', t: '> 200', m: 5 },
            { l: 'b', t: '100–200', m: 4 },
            { l: 'c', t: '25–99', m: 2 },
            { l: 'd', t: '< 25', m: 1 },
          ],
        },
        {
          num: '5',
          text: "How clearly defined are the project's major milestones and deliverables?",
          weight: 20,
          answers: [
            { l: 'a', t: 'Not defined and scheduled', m: 5 },
            { l: 'b', t: 'Defined in detail with logical sequence and included in the schedule', m: 1 },
          ],
        },
        {
          num: '6',
          text: 'How many vendors are involved with this project (applications, infrastructure, network, etc.)?',
          weight: 15,
          answers: [
            { l: 'a', t: 'More than one vendor', m: 5 },
            { l: 'b', t: 'One vendor', m: 3 },
            { l: 'c', t: 'No vendors', m: 1 },
          ],
        },
        {
          num: '7',
          text: 'How many constraints have been identified that influence the selection of a specific solution?',
          weight: 10,
          help: 'Constraints include time, funding, personnel, facilities, and management limitations.',
          answers: [
            { l: 'a', t: '1 or more', m: 5 },
            { l: 'b', t: 'None', m: 1 },
          ],
        },
        {
          num: '8',
          text: 'Are there any open issues relating to integration with other projects that could impact key milestones?',
          weight: 15,
          answers: [
            { l: 'a', t: 'Issues identified that will impact schedule — no contingency plan', m: 5 },
            { l: 'b', t: 'Issues defined in tracking that could impact milestones, but contingency plans implemented', m: 3 },
            { l: 'c', t: 'All dependencies on-schedule with no anticipated impacts', m: 1 },
          ],
        },
        {
          num: '9',
          text: 'Does this project require data conversion?',
          weight: 20,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '10',
          text: 'What percentage of human resources assigned to the project are shared resources with other operations/projects?',
          weight: 15,
          help: 'Percentage = (# shared members) / (total project team)',
          answers: [
            { l: 'a', t: '81–100%', m: 5 },
            { l: 'b', t: '51–80%', m: 4 },
            { l: 'c', t: '21–50%', m: 2 },
            { l: 'd', t: '0–20%', m: 1 },
          ],
        },
        {
          num: '11',
          text: 'How many primary stakeholders are there?',
          weight: 10,
          answers: [
            { l: 'a', t: '> 5', m: 5 },
            { l: 'b', t: '1–5', m: 1 },
          ],
        },
        {
          num: '12a',
          text: 'Does the Project Sponsor have an understanding of IT project management?',
          weight: 10,
          parentLabel: 'Project Sponsor capabilities',
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '12b',
          text: 'Does the Project Sponsor have experience in the business or functional domain?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '13',
          text: "What is the Project Manager's authority over the project?",
          weight: 15,
          help: 'Authority is the formal and legitimate control specified in the charter giving the PM power to act on behalf of the sponsor.',
          answers: [
            { l: 'a', t: 'Little authority', m: 5 },
            { l: 'b', t: 'Some authority', m: 3 },
            { l: 'c', t: 'Complete authority', m: 1 },
          ],
        },
        {
          num: '14',
          text: 'Is the schedule end date fixed (by mandate, contract end date, vendor support expiration, etc.)?',
          weight: 15,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '15',
          text: 'Is there more than one funding source for this project?',
          weight: 10,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
      ],
    },
  },

  // ----------------------------------------------------------
  // PLANNING PHASE
  // ----------------------------------------------------------
  {
    id: 'planning',
    name: 'Planning Phase',
    order: 3,
    desc: 'Performed at the end of the Planning Phase. Reviews planning documents and previous assessments. Establishes requirements for Execution.',
    risk: {
      totalWeight: 190,
      questions: [
        {
          num: '1a',
          text: 'Is the project dependent on data from other sources?',
          weight: 5,
          parentLabel: 'Data Dependency',
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '1b',
          text: 'Is the project dependent on data that is currently not available?',
          weight: 5,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '2',
          text: 'Is the project going to be reliant on a sole vendor?',
          weight: 10,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '3',
          text: 'Will the primary solutions vendor support the technical solution after project completion?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '4a',
          text: 'What is the anticipated involvement of End Users with System Requirements and Design?',
          weight: 10,
          parentLabel: 'End user anticipated involvement',
          answers: [
            { l: 'a', t: 'Minimal or no user involvement', m: 5 },
            { l: 'b', t: 'Play minor roles', m: 4 },
            { l: 'c', t: 'Highly involved', m: 2 },
            { l: 'd', t: 'End user involvement is not required', m: 1 },
          ],
        },
        {
          num: '4b',
          text: 'What is the anticipated involvement of End Users with User Acceptance Testing?',
          weight: 10,
          answers: [
            { l: 'a', t: 'Minimal or no end user involvement', m: 5 },
            { l: 'b', t: 'Play minor roles with testing', m: 4 },
            { l: 'c', t: 'Highly involved with testing', m: 2 },
            { l: 'd', t: 'End user does not interact with the system', m: 1 },
          ],
        },
        {
          num: '5',
          text: 'Are exit criteria established for each project phase?',
          weight: 9,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '6',
          text: 'Does the project schedule incorporate incremental and comprehensive stakeholder reviews of project deliverables?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '7',
          text: 'Are acceptance criteria identified for all deliverables?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '8',
          text: 'If a vendor implementation is required, has the vendor successfully implemented the selected solution in another organization?',
          weight: 6,
          answers: [
            { l: 'a', t: 'Vendor has never implemented the selected solution', m: 5 },
            { l: 'b', t: 'Vendor has provided references indicating successful implementation', m: 3 },
            { l: 'c', t: 'A vendor is not required for implementation', m: 1 },
          ],
        },
        {
          num: '9',
          text: 'Is there contingency built into the project schedule to accommodate the mitigation of schedule risks?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '10',
          text: 'Are appropriate sourcing lead times built into the schedule?',
          weight: 10,
          help: 'Examples include lead times for procurements and Requests for Service.',
          answers: [
            { l: 'a', t: 'Not built into the schedule', m: 5 },
            { l: 'b', t: 'Estimated and scheduled', m: 1 },
          ],
        },
        {
          num: '11a',
          text: "Does the project's governance process include a defined change management process to handle changing requirements?",
          weight: 10,
          parentLabel: 'Change management process',
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '11b',
          text: 'Does the project have routine change management meetings?',
          weight: 5,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '12',
          text: 'Are quality assurance methods defined?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '13',
          text: 'Have appropriate SMEs been engaged to support the project (legal, procurement, security, budget, technology, business, etc.)?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Some input is still needed', m: 3 },
            { l: 'c', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '14',
          text: 'Is there a documented timeline for the next phase of the project?',
          weight: 10,
          answers: [
            { l: 'a', t: 'Project schedule is not yet developed', m: 5 },
            { l: 'b', t: 'Completed at the milestone level', m: 4 },
            { l: 'c', t: 'Detailed schedule complete, but not yet baselined', m: 2 },
            { l: 'd', t: 'Detailed schedule complete and baselined', m: 1 },
          ],
        },
        {
          num: '15',
          text: 'Are all necessary resources (equipment, software, office space, etc.) for the next phase readily available?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '16',
          text: 'Does the Organizational Change Management Plan address impacts to the business?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '17',
          text: 'Have the costs associated with the organizational changes been identified and budgeted?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 3 },
            { l: 'c', t: 'Not applicable', m: 1 },
          ],
        },
        {
          num: '18',
          text: 'Does the Project Plan clearly identify integration requirements with other systems or dependencies on other projects outside the direct control of the project team?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
      ],
    },
    complexity: {
      totalWeight: 181,
      questions: [
        {
          num: '1a',
          text: 'Will the solution drive a need for an increased level of testing from original projections?',
          weight: 5,
          parentLabel: 'In order to meet requirements, will the project solution drive a need for:',
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '1b',
          text: 'Less flexibility in the project schedule?',
          weight: 5,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '1c',
          text: 'More rigid development and internal project processes?',
          weight: 5,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '2',
          text: 'Has the complexity of the project required additional efforts to monitor scope / schedule / cost or quality parameters?',
          weight: 5,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '3',
          text: 'How many stakeholders need separate or unique communications?',
          weight: 8,
          help: 'Unique communications refers to any individual or tailored communications with any stakeholder or group.',
          answers: [
            { l: 'a', t: 'Four or more', m: 5 },
            { l: 'b', t: 'One to three', m: 3 },
            { l: 'c', t: 'None', m: 1 },
          ],
        },
        {
          num: '4',
          text: 'Are there clear lines of authority and accountability for tasks and deliverables within the project team?',
          weight: 5,
          help: 'Clear lines are those that are apparent, easily perceived, and free from confusion, doubt, or ambiguity.',
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '5',
          text: 'How many work packages are associated with the project?',
          weight: 15,
          help: 'Work defined at the lowest level of the WBS for which cost and duration can be estimated and managed.',
          answers: [
            { l: 'a', t: '> 200', m: 5 },
            { l: 'b', t: '101–200', m: 4 },
            { l: 'c', t: '51–100', m: 2 },
            { l: 'd', t: '1–50', m: 1 },
          ],
        },
        {
          num: '6',
          text: 'Does the project staff have experience with the selected system development lifecycle methodology?',
          weight: 15,
          help: 'Common methodologies: Agile, Waterfall, Spiral Development, Prototyping, Incremental, RAD, etc.',
          answers: [
            { l: 'a', t: 'Requires training for the selected methodology', m: 5 },
            { l: 'b', t: 'Knowledge of, but limited experience with the methodology', m: 3 },
            { l: 'c', t: 'Extensive experience with the methodology', m: 1 },
          ],
        },
        {
          num: '7',
          text: 'Are there any new requirements determined after Project Planning that will drive a need for additional funding?',
          weight: 10,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '8',
          text: 'Does the project team require any additional training to be effective (technical, functional, or business skills)?',
          weight: 15,
          answers: [
            { l: 'a', t: 'All require training', m: 5 },
            { l: 'b', t: 'Most require training', m: 4 },
            { l: 'c', t: 'Some require training', m: 2 },
            { l: 'd', t: 'None require training', m: 1 },
          ],
        },
        {
          num: '9',
          text: 'Do the project team members have experience with an IT project of this magnitude and scope?',
          weight: 15,
          answers: [
            { l: 'a', t: 'None have experience', m: 5 },
            { l: 'b', t: 'Some have experience', m: 4 },
            { l: 'c', t: 'Most have experience', m: 2 },
            { l: 'd', t: 'All have experience', m: 1 },
          ],
        },
        {
          num: '10',
          text: 'Do the project team members have experience working together as a project team?',
          weight: 15,
          answers: [
            { l: 'a', t: 'No team members have experience working together', m: 5 },
            { l: 'b', t: 'Some team members have experience working together', m: 3 },
            { l: 'c', t: 'All team members have experience working together', m: 1 },
          ],
        },
        {
          num: '11',
          text: 'Is the size of the project team appropriate for the size and complexity of the project effort?',
          weight: 10,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '12',
          text: 'What is the size of the project team?',
          weight: 15,
          answers: [
            { l: 'a', t: '> 20 project team members', m: 5 },
            { l: 'b', t: 'Between 5 and 20 members', m: 3 },
            { l: 'c', t: '< 5 project team members', m: 1 },
          ],
        },
        {
          num: '13',
          text: 'Are appropriately skilled resources available for the next phase of the project?',
          weight: 15,
          answers: [
            { l: 'a', t: 'Not available for all roles; significant preemption or high turnover anticipated', m: 5 },
            { l: 'b', t: 'Available; minimal turnover or preemption expected', m: 1 },
          ],
        },
        {
          num: '14',
          text: 'What percentage of the project team members are co-located?',
          weight: 8,
          answers: [
            { l: 'a', t: '< 25% in same location', m: 5 },
            { l: 'b', t: '25–49% in same location', m: 4 },
            { l: 'c', t: '50–90% in same location', m: 2 },
            { l: 'd', t: '> 90% in same location', m: 1 },
          ],
        },
        {
          num: '15',
          text: 'How would you evaluate the complexity of the business processes impacted by the project?',
          weight: 15,
          help: 'Consider number of inputs, steps, people involved, and outputs.',
          answers: [
            { l: 'a', t: 'High complexity', m: 5 },
            { l: 'b', t: 'Moderate complexity', m: 3 },
            { l: 'c', t: 'Minimal complexity', m: 1 },
          ],
        },
      ],
    },
  },

  // ----------------------------------------------------------
  // EVENT-DRIVEN ASSESSMENT
  // ----------------------------------------------------------
  {
    id: 'event_driven',
    name: 'Event-Driven Assessment',
    order: 4,
    desc: 'Performed if the project experiences a significant change (cost, schedule, or scope) from the project baseline. Uses Planning Phase complexity score.',
    risk: {
      totalWeight: 160,
      questions: [
        {
          num: '1',
          text: 'To what degree are stakeholders impacting the schedule by not providing timely decisions?',
          weight: 15,
          answers: [
            { l: 'a', t: 'Time required for critical decisions exceeds available schedule', m: 5 },
            { l: 'b', t: 'Critical decisions are resolved within available schedule', m: 1 },
          ],
        },
        {
          num: '2',
          text: 'Has an assumption used for planning and management of the project been proven invalid?',
          weight: 15,
          answers: [
            { l: 'a', t: 'Yes, and there is an impact to the project', m: 5 },
            { l: 'b', t: 'Yes, but minimal-to-no impact to the project', m: 3 },
            { l: 'c', t: 'No', m: 1 },
          ],
        },
        {
          num: '3',
          text: 'Is the project making progress in its current phase?',
          weight: 20,
          answers: [
            { l: 'a', t: 'Progress is behind schedule by 10% or more', m: 5 },
            { l: 'b', t: 'Progress is on or ahead of schedule', m: 1 },
          ],
        },
        {
          num: '4',
          text: 'Is the project being managed in compliance with the project management plan?',
          weight: 15,
          answers: [
            { l: 'a', t: 'No, or the project management plan was inadequate', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '5',
          text: 'Has requirements elaboration resulted in a requirements variance sufficient to force changes to project schedule, scope, or cost?',
          weight: 20,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '6',
          text: 'Has project testing criteria and methodology been verified and validated?',
          weight: 15,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '7',
          text: 'Is the project team effectively executing the project through well defined, repeatable processes?',
          weight: 15,
          answers: [
            { l: 'a', t: 'No', m: 5 },
            { l: 'b', t: 'Yes', m: 1 },
          ],
        },
        {
          num: '8a',
          text: 'Will the project require an increased level of testing from projections?',
          weight: 15,
          parentLabel: 'Will the project require:',
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '8b',
          text: 'An increase in the duration of the project schedule?',
          weight: 15,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
        {
          num: '8c',
          text: "An increase in the project's baselined cost?",
          weight: 15,
          answers: [
            { l: 'a', t: 'Yes', m: 5 },
            { l: 'b', t: 'No', m: 1 },
          ],
        },
      ],
    },
    complexity: null,
  },
];
